import type { ChangeEvent, MouseEvent } from 'react';
import { forwardRef, type InputHTMLAttributes, useState } from 'react';

import { IconButton } from '@/components/ui/Button/IconButton';
import { Close, Search } from '@/components/ui/Icons';
import { Tooltip } from '@/new_src/view/components/Tooltip';

import styled from 'styled-components';
import { defaultTheme } from '@/theme';

type Size = 'sm' | 'md' | 'lg' | 'xl';

const SIZE_MAP = {
  sm: {
    height: '28px',
    leftPadding: '8px',
    rightPadding: '8px',
    withRemoveIconRightPadding: '24px',
    fontSize: '12px',
    gap: '4px',
    iconSize: '12px',
    removeIconRight: '4px',
  },
  md: {
    height: '32px',
    leftPadding: '12px',
    rightPadding: '12px',
    withRemoveIconRightPadding: '34px',
    fontSize: '14px',
    gap: '4px',
    iconSize: '16px',
    removeIconRight: '2px',
  },
  lg: {
    height: '40px',
    leftPadding: '16px',
    rightPadding: '16px',
    withRemoveIconRightPadding: '40px',
    fontSize: '16px',
    gap: '8px',
    iconSize: '20px',
    removeIconRight: '8px',
  },
  xl: {
    height: '48px',
    leftPadding: '20px',
    rightPadding: '20px',
    withRemoveIconRightPadding: '44px',
    fontSize: '16px',
    gap: '8px',
    iconSize: '20px',
    removeIconRight: '12px',
  },
} as const;

const blue = defaultTheme.mantineColors.blue;
const red = defaultTheme.mantineColors.red;
const gray = defaultTheme.mantineColors.gray;

const BORDER_COLOR_MAP = {
  default: gray[4],
  hover: gray[6],
  focused: blue[6],
  disabled: gray[3],
  error: red[6],
  errorFocused: red[6],
};

type TextfieldProps = {
  value: string;
  size: Size;
  width?: string;
  isError?: boolean;
  isActive?: boolean;
  renderLeftIcon?: ({ width, height }: { width: string; height: string }) => React.ReactNode;
  withSearchIcon?: boolean;
  disabled?: boolean;
  withRemoveIcon?: boolean;
  onRemoveClick?: () => void;
} & Omit<InputHTMLAttributes<HTMLInputElement>, 'size' | 'value'>;

export const Textfield = forwardRef<HTMLInputElement, TextfieldProps>(
  (
    {
      value,
      size,
      width,
      withSearchIcon = false,
      withRemoveIcon = true,
      isError = false,
      isActive = false,
      disabled = false,
      onRemoveClick,
      renderLeftIcon,
      onMouseEnter,
      onChange,
      className,
      ...props
    },
    ref
  ) => {
    const [isOverflowing, setIsOverflowing] = useState(false);

    const handleMouseEnter = (e: MouseEvent<HTMLInputElement>) => {
      const { scrollWidth, clientWidth } = e.currentTarget;
      setIsOverflowing(scrollWidth > clientWidth);
      onMouseEnter?.(e);
    };

    const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
      setIsOverflowing(false);
      onChange?.(e);
    };

    const isRemoveIconExist = value.length > 0 && !disabled && withRemoveIcon;

    return (
      <Container
        className={className}
        $size={size}
        $disabled={disabled}
        $withRemoveIcon={!!isRemoveIconExist}
        $width={width}
        $isError={isError}
        $isActive={isActive}
      >
        {renderLeftIcon
          ? renderLeftIcon({
              width: SIZE_MAP[size].iconSize,
              height: SIZE_MAP[size].iconSize,
            })
          : withSearchIcon && (
              <SearchIcon
                width={SIZE_MAP[size].iconSize}
                height={SIZE_MAP[size].iconSize}
                fill={disabled ? gray[5] : gray[8]}
              />
            )}
        <Tooltip label={value} disabled={!isOverflowing} position="top-start">
          <Input
            $size={size}
            ref={ref}
            value={value}
            disabled={disabled}
            onMouseEnter={handleMouseEnter}
            onChange={handleChange}
            {...props}
          />
        </Tooltip>
        {isRemoveIconExist && (
          <IconButton
            type="button"
            style={{
              position: 'absolute',
              right: SIZE_MAP[size]['removeIconRight'],
            }}
            variant="gray-transparent"
            size={size === 'sm' ? 'xs' : 'md'}
            renderIcon={({ width, height }) => <Close width={width} height={height} />}
            ariaLabel="입력 내용 지우기"
            onClick={onRemoveClick}
          />
        )}
      </Container>
    );
  }
);

const Container = styled.div<{
  $size: Size;
  $disabled: boolean;
  $withRemoveIcon: boolean;
  $width: string | undefined;
  $isError: boolean;
  $isActive: boolean;
}>`
  position: relative;
  height: ${({ $size }) => SIZE_MAP[$size]['height']};
  min-height: ${({ $size }) => SIZE_MAP[$size]['height']};
  width: ${({ $width }) => $width || '100%'};
  display: flex;
  align-items: center;
  box-sizing: border-box;
  border-radius: 4px;
  background-color: ${({ theme, $disabled }) =>
    $disabled ? theme.mantineColors.gray[2] : theme.mantineColors.gray[1]};
  gap: ${({ $size }) => SIZE_MAP[$size]['gap']};
  padding-top: 0;
  padding-bottom: 0;
  padding-left: ${({ $size }) => SIZE_MAP[$size]['leftPadding']};
  padding-right: ${({ $size, $withRemoveIcon }) =>
    $withRemoveIcon ? SIZE_MAP[$size]['withRemoveIconRightPadding'] : SIZE_MAP[$size]['rightPadding']};
  box-shadow: ${({ $disabled, $isError }) =>
    $disabled
      ? `inset 0 0 0 1px ${BORDER_COLOR_MAP.disabled}`
      : $isError
      ? `inset 0 0 0 1px ${BORDER_COLOR_MAP.error}`
      : `inset 0 0 0 1px ${BORDER_COLOR_MAP.default}`};
  cursor: ${({ $disabled }) => ($disabled ? 'not-allowed' : 'text')};
  &:hover {
    box-shadow: ${({ $disabled, $isError }) =>
      $disabled
        ? `inset 0 0 0 1px ${BORDER_COLOR_MAP.disabled}`
        : $isError
        ? `inset 0 0 0 1px ${BORDER_COLOR_MAP.error}`
        : `inset 0 0 0 1px ${BORDER_COLOR_MAP.hover}`};
  }
  &:focus-within {
    box-shadow: ${({ $disabled, $isError }) =>
      $disabled
        ? `inset 0 0 0 1px ${BORDER_COLOR_MAP.disabled}`
        : $isError
        ? `inset 0 0 0 2px ${BORDER_COLOR_MAP.error}`
        : `inset 0 0 0 2px ${BORDER_COLOR_MAP.focused}`};
  }
  ${({ $isActive, $disabled, $isError }) =>
    $isActive && !$disabled && !$isError && `box-shadow: inset 0 0 0 2px ${BORDER_COLOR_MAP.focused}`};
`;

const SearchIcon = styled(Search)`
  flex-shrink: 0;
`;

const Input = styled.input<{ $size: Size }>`
  width: 100%;
  height: 100%;
  flex-grow: 1;
  outline: none;
  border: none;
  font-size: ${({ $size }) => SIZE_MAP[$size]['fontSize']};
  line-height: 160%;
  background: transparent;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;

  &:disabled {
    background-color: transparent;
    color: ${({ theme }) => theme.mantineColors.gray[5]};
    cursor: not-allowed;
  }

  &:disabled::placeholder {
    color: ${({ theme }) => theme.mantineColors.gray[5]};
  }

  &::placeholder {
    color: ${({ theme }) => theme.mantineColors.gray[6]};
  }
`;
